import {inject, Injectable} from '@angular/core';
import {CommonsService} from './commons.service';
import {ChangeTrackerService} from './change-tracker.service';
import {ObjectFieldTraverseService} from './object-field-traverse.service';
import {FieldParameters} from './definitions/field-parameters';
import {CmsQueueService} from './cms-queue.service';
import {MetaField} from './definitions/meta-field';
import {IfCondition, FieldIf, IfThenType, IfType} from './definitions/field-if';
import {UserData} from './definitions/user-data';
import {BaseModel} from './definitions/base-model';
import {ModelsService} from './models.service';
import {CmsApiService} from './cms-api.service';
import {UserCacheService} from './user-cache.service';
import {LoggerService} from './logger.service';
import {FieldValueService} from './field-value.service';
import {UserPrivilege} from '../administration/admin-user/User';
import {FieldType} from "./definitions/field-type.enum";
import {FieldKeyService} from "./field-key.service";

export interface DisabledResult {
  disabled: boolean;
  reason: string;
}

export interface RunIfResult {
  result: boolean;
  resultText: string;
  hadIfType: boolean;
}

enum ConditionType {
  CHECK_WITH_BACKEND = 'check_with_backend',
  // COMPARE_FIELD_VALUE = 'compare_field_value',
  COMPARE_WITH_OTHER_FIELD = 'compare_with_other_field',
  EDITING = 'editing',
  EDITION = 'edition',
  // FIELD_VALUE_IN_VALUES = 'field_value_in_values',
  IS_ADMIN_USER = 'is_admin_user',
  NOT_EDITING = 'not_editing',
  OBJECT_HAS_CHANGES = 'object_has_changes',
  OBJECT_HAS_NO_CHANGES = 'object_has_no_changes',
}

@Injectable({
  providedIn: 'root'
})
export class FieldConditionService {
  // Injections
  private readonly changeTracker: ChangeTrackerService = inject(ChangeTrackerService);
  private readonly cms: CmsApiService = inject(CmsApiService);
  private readonly cmsQueue: CmsQueueService = inject(CmsQueueService);
  private readonly commons: CommonsService = inject(CommonsService);
  private readonly fieldValueService: FieldValueService = inject(FieldValueService);
  private readonly logger: LoggerService = inject(LoggerService);
  private readonly modelsService: ModelsService = inject(ModelsService);
  private readonly objectFieldTraverse: ObjectFieldTraverseService = inject(ObjectFieldTraverseService);
  private readonly userCacheService: UserCacheService = inject(UserCacheService);
  private readonly fieldKeyService: FieldKeyService = inject(FieldKeyService);


  myUserData: UserData;
  mySettings: any;
  gettingSettings = false;
  fieldConditions: { [id: string]: FieldIf[] }

  private backendAnswers = {};
  private backendAnswerPurgeTime = {};

  setFieldConditions() {
    this.cms.getFieldConditions().then(fieldConditions => {
      this.fieldConditions = fieldConditions
    });
  }

  runIf(ifType: IfType, fieldParameters: FieldParameters, setValueCallback?: any): RunIfResult {
    const ifItem = this.getIfByType(fieldParameters, ifType);
    let res: boolean;
    let resultText = '';
    if (ifItem) {
      res = this.runIfItem(ifItem, fieldParameters);
      if (res && ifItem.field_warning_text) {
        resultText = ifItem.field_warning_text;
      }
    } else {
      res = this.getDefaultRes(ifType);
    }

    if (ifItem) {
      if (ifItem.if_then && res) {
        this.runIfThenElseClauses(ifItem, fieldParameters, setValueCallback);
      } else if (ifItem.if_else) {
        this.runIfElseClauses(ifItem, fieldParameters, setValueCallback);
      }
    }
    return {
      result: res,
      resultText: resultText,
      hadIfType: !!ifItem
    };
  }

  runIfThenElseClauses(ifItem: FieldIf, fieldParameters: FieldParameters, setValueCallback?: any) {
    if (ifItem.if_then) {
      for (const ifThen of ifItem.if_then) {
        this.runIfThenElseClause(ifThen.if_then_type, ifThen.value, fieldParameters, setValueCallback);
      }
    }
  }

  runIfElseClauses(ifItem: FieldIf, fieldParameters: FieldParameters, setValueCallback?: any) {
    if (ifItem.if_else) {
      for (const ifElse of ifItem.if_else) {
        this.runIfThenElseClause(ifElse.if_else_type, ifElse.value, fieldParameters, setValueCallback);
      }
    }
  }

  runIfItem(ifItem: FieldIf, fieldParameters: FieldParameters): boolean {
    let conditions: Array<IfCondition>;
    let res: boolean;
    conditions = ifItem.conditions || [];
    for (const cond of conditions) {
      res = this.runCompare(cond, fieldParameters);
      if (res && ifItem.operator === 'or') {
        break;
      }
      if (!res && ifItem.operator !== 'or') {
        break;
      }
    }
    return res;
  }

  runIfDisabled(fieldParameters: FieldParameters): DisabledResult {
    let reason = '';
    let isDisabled = false;
    const ifItem = this.getIfByType(fieldParameters, IfType.DISABLE);
    if (ifItem) {
      this.runIfItem(ifItem, fieldParameters);
      isDisabled = this.runIf(IfType.DISABLE, fieldParameters).result;
      if (isDisabled && ifItem.reason_field) {
        reason = this.getCompareFieldValue(ifItem.reason_field, fieldParameters);
      }
    }
    return {
      reason: reason,
      disabled: isDisabled
    };
  }

  getFieldConditions(field: MetaField): FieldIf[] {
    if (!field) {
      return [];
    }
    let res: FieldIf[];
    if (field.field_ifs) {
      res = field.field_ifs;
    } else if (field.conditions_id) {
      if (this.fieldConditions) {
        res = this.fieldConditions[field.conditions_id];
      } else {
        this.setFieldConditions();
        res = [];
      }
    }
    return res;
  }

  private getCompareValue(value: any) {
    let res = value;
    if (typeof res === 'string' && res.includes('::')) {
      res = this.getSpecialFieldValue(res);
    }
    if (res === undefined) {
      res = null;
    }
    return res;
  }

  private getSpecialFieldValue(sourceField: string) {
    let res = null;
    const specialInfo = [
      {prefix: 'user::', specialObject: this.userData},
      {prefix: 'settings::', specialObject: this.settings}
    ];
    for (const comp of specialInfo) {
      if (sourceField.startsWith(comp.prefix)) {
        if (comp.specialObject) {
          res = this.getSpecialFieldValueFromObject(sourceField, comp.specialObject);
        } else {
          this.logger.warn(`Special object not set yet: ${comp.prefix}`);
        }
        break;
      }
    }
    return res;
  }

  private getSpecialFieldValueFromObject(fields: string, specialObject: any) {
    const fieldsSplit = fields.split('::')[1].split('.');
    let obj = specialObject;
    for (const field of fieldsSplit) {
      obj = obj[field];
    }
    return obj;
  }

  private getValueFromObject(fieldName: string, fieldParameters: FieldParameters, object: BaseModel) {
    const res = {
      found: false,
      value: null
    };
    if (!object) {
      return res;
    }
    const objectAndMetaFieldRes = this.getObjectAndMetaField(object, fieldName);
    let metaField = objectAndMetaFieldRes.metaField;
    let foundObj = objectAndMetaFieldRes.object;
    fieldName = objectAndMetaFieldRes.fieldName;
    let parentMetaField: MetaField = null;
    if (!metaField && fieldParameters.field.path) {
      const fieldPathSplit = fieldParameters.field.path.split('.');
      for (const element of fieldPathSplit) {
        const objAndParentMetaRes = this.getObjectAndParentMetaField(fieldParameters, foundObj, element);
        foundObj = objAndParentMetaRes.object;
        parentMetaField = objAndParentMetaRes.parentMetaField;
        if (objAndParentMetaRes.breakIt) {
          break;
        }
      }
      if (parentMetaField && foundObj) {
        metaField = this.getMetaField([foundObj], fieldName);
      }
    }
    this.setResultOfGetValueFromObject(res, metaField, foundObj, fieldName);
    return res;
  }

  private getObjectAndMetaField(object: any, fieldName: string) {
    let metaField: MetaField;
    let foundObj: any = object;
    if (fieldName.includes('.')) {
      const nameSplit = fieldName.split('.');
      metaField = this.getMetaField([object], nameSplit[0]);
      if (metaField) {
        foundObj = object[nameSplit[0]];
        fieldName = nameSplit[1];
      }
    } else {
      metaField = this.getMetaField([object], fieldName);
    }
    return {
      object: foundObj,
      metaField: metaField,
      fieldName: fieldName
    }
  }

  private getObjectAndParentMetaField(fieldParameters: FieldParameters, object: any, element: string) {
    let parentMetaField: MetaField;
    object = this.checkGetArrayObject(fieldParameters, object);
    parentMetaField = this.getMetaField([object], element);
    let breakIt = false;
    if (parentMetaField) {
      object = object[element];
      if (!object) {
        breakIt = true
      } else if (typeof object !== 'object') {
        parentMetaField = null;
        breakIt = true
      }
    } else {
      breakIt = true
    }
    if (Array.isArray(object)) {
      object = object[fieldParameters.index];
    }
    return {
      object: object,
      parentMetaField: parentMetaField,
      breakIt: breakIt
    }
  }

  private setResultOfGetValueFromObject(res: any, metaField: MetaField, foundObj: any, fieldName: string) {
    if (metaField) {
      res.found = true;
      if (Array.isArray(foundObj)) {
        res.value = [];
        for (const item of foundObj) {
          if (item?.[fieldName] !== undefined) {
            res.value.push(item[fieldName]);
          }
        }
      } else {
        res.value = foundObj ? foundObj[fieldName] : null;
      }
    }
  }

  private checkGetArrayObject(fieldParameters: FieldParameters, obj: any) {
    if (Array.isArray(obj)) {
      if (fieldParameters.index || fieldParameters.index === 0) {
        obj = obj[fieldParameters.index];
      } else {
        this.logger.error('Unable to get array element');
      }
    }
    return obj
  }

  private getCompareFieldValue(fieldName: string, fieldParameters: FieldParameters) {
    let res = null;
    let valueInfo: any;
    const objects = [fieldParameters.object, fieldParameters.grandParentObject, fieldParameters.rootObject];
    for (const element of objects) {
      valueInfo = this.getValueFromObject(fieldName, fieldParameters, element);
      if (valueInfo.found) {
        res = valueInfo.value;
        break;
      }
    }
    if (valueInfo && !valueInfo.found && fieldName !== 'create_multiple') {
      this.logger.warn(`Unable to find compare value for ${fieldName}`);
    }
    return res;
  }

  private runCompare(cond: IfCondition, fieldParameters: FieldParameters) {
    let res: boolean;
    let otherValue = cond.value;
    const edit = fieldParameters.edit || false;

    const fieldVal = this.getConditionFieldVal(cond, fieldParameters);
    if (cond.compare_field) {
      otherValue = this.getFieldValue(cond.compare_field, fieldParameters);
    }
    if (cond.cond_type === ConditionType.EDITING) {
      res = edit;
    } else if (cond.cond_type === ConditionType.NOT_EDITING) {
      res = !edit;
    } else if (cond.cond_type === ConditionType.OBJECT_HAS_CHANGES) {
      res = this.changeTracker.objectHasChanges(fieldParameters.rootObject);
    } else if (cond.cond_type === ConditionType.OBJECT_HAS_NO_CHANGES) {
      res = !this.changeTracker.objectHasChanges(fieldParameters.rootObject);
    } else if (cond.cond_type === ConditionType.EDITION) {
      res = this.compareEdition(cond);
    } else if (cond.cond_type === ConditionType.COMPARE_WITH_OTHER_FIELD) {
      res = this.compareValue(cond, cond.value, otherValue);
    } else if (cond.cond_type === ConditionType.IS_ADMIN_USER) {
      res = this.compareIsAdminUser(cond);
    } else if (cond.cond_type === ConditionType.CHECK_WITH_BACKEND) {
      res = this.checkBackendCondition(cond);
    } else if (cond.values) {
      res = this.compareValues(cond, fieldVal);
    } else {
      res = this.compareValue(cond, fieldVal, otherValue);
    }
    return res;
  }

  private getConditionFieldVal(cond: IfCondition, fieldParameters: FieldParameters): any {
    let fieldVal: any;
    if (cond.field) {
      if (cond.field.includes('::')) {
        fieldVal = this.getFieldValue(cond.field, fieldParameters);
      } else {
        fieldVal = this.getCompareFieldValue(cond.field, fieldParameters);
      }
    }
    return fieldVal;
  }

  private compareEdition(cond: IfCondition) {
    let res = false;
    if (cond.values) {
      res = this.compareValues(cond, this.userData?.edition?.toUpperCase());
    } else if (cond.value) {
      res = this.compareValue(cond, cond.value, this.userData?.edition?.toUpperCase());
    } else {
      this.logger.warn('Do not know what to do here 😬')
    }
    return res;
  }

  private compareIsAdminUser(condition: IfCondition): boolean {
    const isAdminUser = this.userData?.rights_level as UserPrivilege === UserPrivilege.ADMIN;
    const compareValue = true;
    return this.commons.compareValues(isAdminUser, condition.comparator, compareValue);
  }

  private compareValue(condition: IfCondition, value: any, otherValue: any): boolean {
    const compValue1 = this.getCompareValue(value);
    const compValue2 = this.getCompareValue(otherValue);
    return this.commons.compareValues(compValue1, condition.comparator, compValue2);
  }

  private compareValues(condition: IfCondition, fieldValueIn: any): boolean {
    let res: boolean;
    const fieldValues = Array.isArray(fieldValueIn) ? fieldValueIn : [fieldValueIn];
    let fulfilled = false;
    for (const conditionValue of condition.values) {
      for (const fieldValue of fieldValues) {
        res = this.compareValue(condition, conditionValue, fieldValue);
        fulfilled = this.checkConditionFulfilled(res, condition);
        if (fulfilled) {
          break;
        }
      }
      if (fulfilled) {
        break;
      }
    }
    return res;
  }

  private checkConditionFulfilled(result: boolean, condition: IfCondition) {
    let fulfilled = false;
    if (condition.comparator === '!=') {
      if (!result) {
        fulfilled = true;
      }
    } else if (result) {
      fulfilled = true;

    }
    return fulfilled;
  }

  private getIfByType(fieldParameters: FieldParameters, ifType: IfType): FieldIf {
    let res: FieldIf;
    if (fieldParameters.field['$$hasIf']?.[ifType] === undefined) {
      this.setDefaultHasIf(fieldParameters);
      const field = this.getFieldWithConditions(fieldParameters);
      const ifs = this.getFieldConditions(field);
      if (!ifs?.length) {
        return fieldParameters.field['$$hasIf'][ifType];
      }
      for (const ifItem of ifs || []) {
        fieldParameters.field['$$hasIf'][ifItem.if_type] = ifItem;
        if (ifItem.if_type === ifType) {
          res = ifItem
        }
      }
    } else {
      res = fieldParameters.field['$$hasIf'][ifType];
    }
    return res;
  }

  private getFieldWithConditions(fieldParameters: FieldParameters): MetaField {
    let res: MetaField;
    if (fieldParameters.field.conditions_id) {
      res = fieldParameters.field;
    } else if (fieldParameters.field.parent_name) {
      const parentField = this.getMetaField(
        [fieldParameters.grandParentObject, fieldParameters.object],
        fieldParameters.field.parent_name);
      if (parentField.conditions_id && parentField.field_type !== FieldType.ARRAY) {
        res = parentField;
      }
    }
    return res;
  }

  private setDefaultHasIf(fieldParameters: FieldParameters) {
    if (!fieldParameters.field['$$hasIf']) {
      fieldParameters.field['$$hasIf'] = {
        show: false,
        edit: false,
        disable: false,
        field_warning: false
      }
    }
  }

  private getFieldValue(fieldIn: string, fieldParameters: FieldParameters) {
    let fieldVal: any;
    const field = this.getReplaceField(fieldIn, fieldParameters);
    if (field === null) {
      return null;
    }
    if (field.includes('::')) {
      fieldVal = this.getSpecialFieldValue(field);
    } else {
      fieldVal = this.getFieldValueFromModels(field, fieldParameters);
    }
    return fieldVal;
  }

  private getReplaceField(field: string, fieldParameters: FieldParameters) {
    let res = field;
    const start = field.indexOf('{');
    if (start > -1) {
      const end = field.indexOf('}');
      if (end > start) {
        const searchField = field.substring(start + 1, end);
        const replaceField = this.getFieldValue(searchField, fieldParameters);
        if (replaceField === null) {
          res = null;
        } else {
          res = field.substring(0, start) + replaceField +
            field.substring(end + 1);
        }
      }
    }
    return res;
  }

  private getFieldPath(fieldName: string, fieldParameters: FieldParameters) {
    let fieldKey = '';
    const field = this.getFieldKeyInfo(fieldName, fieldParameters);
    if (field) {
      fieldKey = this.fieldKeyService.getFieldKeyWhileDrawingInputs(
        field, fieldParameters.index, fieldParameters.parentIndex, fieldParameters.grandparentIndex);
      if (!fieldKey.includes(fieldName)) {
        // Field name is a compare field. Need to exchange the last part of the field key with the compare field name
        const lastPathSep = fieldKey.lastIndexOf('.');
        if (lastPathSep !== -1) {
          fieldKey = fieldKey.substring(0, lastPathSep + 1) + fieldName;
        }
      }
    } else {
      this.logger.warn('Field info not found for ' + fieldName);
    }
    return fieldKey;
  }

  private getFieldKeyInfo(fieldName: string, fieldParameters: FieldParameters): MetaField {
    let field: MetaField;
    if (fieldParameters.field.path) {
      const traverseRes = this.objectFieldTraverse.traverseObjectByPath(
        fieldParameters.rootObject, fieldParameters.field.path, 0);
      const parentObject = traverseRes.subObject;
      if (parentObject && parentObject instanceof BaseModel) {
        const fieldMeta = this.getMetaField([parentObject], fieldName);
        if (fieldMeta) {
          field = {key: traverseRes.parentKey + '.' + fieldName} as MetaField;
        } else {
          this.logger.warn('Field ' + fieldName + ' not found in path ' + fieldParameters.field.path);
        }
      } else {
        this.logger.warn('No sub model found for path ' + fieldParameters.field.path);
      }
    } else {
      const fieldMeta = this.getMetaField([fieldParameters.rootObject], fieldName);
      if (fieldMeta) {
        field = {key: fieldName} as MetaField;
      } else {
        this.logger.warn('Field ' + fieldName + ' not found in root object');
      }
    }
    return field;
  }

  private getMetaField(objects: BaseModel[], fieldName: string): MetaField {
    let res = null;
    for (const object of objects) {
      if (!object) {
        continue;
      }
      const meta = this.getMetaData(object);
      if (meta) {
        res = meta[fieldName];
        if (res) {
          break;
        }
      }
    }
    return res;
  }

  private getMetaData(object: BaseModel) {
    let meta = object.$$meta;
    if (!meta) {
      if (object.object_type) {
        meta = this.modelsService.getModelMeta(object.object_type);
        if (!meta) {
          this.logger.error('Unable to find meta for ' + object.object_type);
        }
      } else {
        this.logger.error('Object has no object type');
      }
    }
    return meta;
  }

  private getFieldValueFromModels(fieldName: string, fieldParameters: FieldParameters) {
    let traverseRes = this.objectFieldTraverse.traverseObjectByPath(fieldParameters.rootObject, fieldName, 0);
    if (traverseRes.subObject === undefined) {
      const fieldPath = this.getFieldPath(fieldName, fieldParameters);
      traverseRes = this.objectFieldTraverse.traverseObjectByPath(fieldParameters.rootObject, fieldPath, 0);
    }
    return traverseRes.subObject;
  }

  private getDefaultRes(ifType: string) {
    const res = {
      disable: false,
      field_warning: false
    }[ifType];
    return res ?? true;
  }

  private runIfThenElseClause(ifThenType: string, value: any, fieldParameters: FieldParameters, setValueCallback?: any) {
    if (ifThenType === IfThenType.SET_DEFAULT_VAL) {
      const fieldName = fieldParameters.field.name;
      if (!fieldParameters.object[fieldName]) {
        this.fieldValueService.setFieldValueAndControlValue(fieldParameters, fieldParameters.object, fieldParameters.field, value).then(() => {
          if (setValueCallback) {
            setValueCallback();
          }
        });
      }
    }
  }

  // This special condition functionality is used for requesting specific backend APIs for information that
  // cannot be provided in any other way. The condition type must be set to "check_with_backend" and the
  // API to be requested must be indicated using the "backend_api" attribute, which is the "question".
  // The API must always return a json object using the following format:
  // { "question": true/false }
  // In the case of the 'global_template_exists' question, the API will return:
  // { "global_template_exists": true/false }
  private checkBackendCondition(cond: IfCondition) {
    let question = cond.backend_api;
    let answer = this.backendAnswers[question];
    if (answer === undefined || this.backendAnswerPurgeTime[question] < (Date.now())) {
      if (answer === undefined) {
        this.backendAnswers[question] = false;
      }
      this.backendAnswerPurgeTime[question] = Date.now() + 2000;
      if (question === 'global_template_exists') {
        this.cms.globalTemplateExists().then(noGlobalExistsRes => {
          this.backendAnswers[question] = noGlobalExistsRes[question];
        });
      }
    }
    return this.backendAnswers[question] === cond.value;
  }

  private get userData() {
    if (!this.myUserData) {
      this.userCacheService.getUserData().then(userData => {
        this.myUserData = userData;
      });
    }
    return this.myUserData;
  }

  private get settings() {
    if (!this.mySettings && !this.gettingSettings) {
      this.gettingSettings = true;
      this.cmsQueue.runCmsFnWithQueue(this.cms.getClientConfig, undefined, false,
        (data: any) => {
          this.mySettings = data;
          this.gettingSettings = false;
        },
        (e: any) => {
          this.logger.error(`Error getting settings: ${e}`);
        }
      ).then();
    }
    return this.mySettings;
  }
}
